import FilterBox from './filter-box';
import FilterBase from './filter-base';
import { hide, show } from '../common';
import { FilterActiveValue } from './active-filters';
import FilterParamStats from './filter-stats';
import { FilterParamItem } from './filter-param-items';

interface FlatItem {
    listId: string;
    name: string;
    element: HTMLElement;
    input: HTMLInputElement;
    linkElement: HTMLElement;
}

export default class CheckboxFlatFilter extends FilterBase {

    private items: FlatItem[];
    private itemsWrapper: HTMLElement;
    private showMoreText: string;
    private showLessText: string;
    private showMoreLinkElement: HTMLElement;
    private defaultItemsDisplayed = 10;

    constructor(element: HTMLElement, filterBox: FilterBox) {
        super(element, filterBox);
    }

    init(): void {
        this.itemsWrapper = this.baseElement.querySelector('[data-component-part="items-wrapper"]');
        this.showMoreText = this.baseElement.dataset.showMoreText;
        this.showLessText = this.baseElement.dataset.showLessText;

        this.items = [...this.baseElement.querySelectorAll('[data-wrapper-list-id]')]
            .map(el => {
                const element = el as HTMLElement;
                const listId = element.dataset.wrapperListId;
                const input = element.querySelector(`[data-list-id="${listId}"]`) as HTMLInputElement;
                const linkElement = element.querySelector(`[data-link-list-id="${listId}"]`) as HTMLElement;
                return { listId, name: input.name, element, input, linkElement };
            });

        this.items.forEach(item => {
            item.input.addEventListener('change', () => this.updateAttributeAndApplyChanges());
            if (item.linkElement) {
                item.linkElement.addEventListener('click', (e) => { e.preventDefault(); this.selectOnly(item.listId); });
            }
        });

        this.applyShowMore();
    }

    protected async setValuesInternal(values: string): Promise<void> {
        const selectedIds = values.split(',');
        this.items.forEach(item => {
            item.input.checked = selectedIds.includes(item.listId);
        });
    }

    protected clearValuesInternal(): void {
        this.items.forEach(item => {
            item.input.checked = false;
        });
    }

    public unselect(value: string): void {
        const item = this.items.find(i => i.listId === value);
        if (item) {
            item.input.checked = false;
        }
        this.updateAttributeAndApplyChanges();
    }

    private selectOnly(listId: string): void {
        this.items.forEach(i => i.input.checked = false);
        const item = this.items.find(i => i.listId === listId);
        if (item) {
            item.input.checked = true;
        }
        this.updateAttributeAndApplyChanges();
    }

    protected getSelectedValuesConcat(): string {
        const selected = this.items.filter(i => i.input.checked).map(i => i.listId);
        return selected.length > 0 ? selected.join(',') : null;
    }

    protected getActiveFilterValues(): FilterActiveValue[] {
        return this.items
            .filter(i => i.input.checked)
            .map(i => <FilterActiveValue>{ value: i.listId, displayName: i.name });
    }

    public setStats(stats: FilterParamStats): void {
        const leafItems = this.getLeafItems(stats.Items);

        if (!leafItems || leafItems.length === 0) {
            hide(this.baseElement);
            return;
        }
        show(this.baseElement);

        const selectedIds = new Set(this.items.filter(i => i.input.checked).map(i => i.listId));
        const wasExpanded = this.isShowMoreExpanded();

        this.itemsWrapper.innerHTML = '';
        this.items = [];

        [...leafItems]
            .sort((a, b) => a.Name.localeCompare(b.Name))
            .forEach(i => this.addItem(i));

        this.items.forEach(item => {
            item.input.checked = selectedIds.has(item.listId);
        });

        this.applyShowMore();
        if (wasExpanded) {
            this.setShowMore(true);
        }

        this.updateActiveFilterValues();
    }

    private getLeafItems(items: FilterParamItem[]): FilterParamItem[] {
        const leaves: FilterParamItem[] = [];
        if (!items || items.length === 0) return leaves;

        const stack = [...items];
        while (stack.length > 0) {
            const current = stack.pop();
            if (!current.FilterValues || current.FilterValues.length === 0) {
                leaves.push(current);
            } else {
                current.FilterValues.forEach(child => stack.push(child));
            }
        }
        return leaves;
    }

    private addItem(item: FilterParamItem): void {
        const element = document.createElement('div');
        element.classList.add('checkbox-value');
        element.dataset.wrapperListId = item.ListId.toString();

        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.classList.add('checkbox-filter-input', 'top-input');
        input.setAttribute('data-component-part', 'top-level-item');
        input.dataset.listId = item.ListId.toString();
        input.name = item.Name;
        const span = document.createElement('span');
        span.dataset.linkListId = item.ListId.toString();
        span.classList.add('checkbox-fake-link');
        span.innerText = item.Name;

        label.appendChild(input);
        label.appendChild(span);
        element.appendChild(label);

        input.addEventListener('change', () => this.updateAttributeAndApplyChanges());
        span.addEventListener('click', (e) => { e.preventDefault(); this.selectOnly(item.ListId.toString()); });

        this.items.push({
            listId: item.ListId.toString(),
            name: item.Name,
            element,
            input,
            linkElement: span
        });

        this.itemsWrapper.appendChild(element);
    }

    // --- Show More / Show Less ---

    private applyShowMore(): void {
        if (this.showMoreLinkElement) {
            this.showMoreLinkElement.remove();
            this.showMoreLinkElement = null;
        }

        if (this.items.length > this.defaultItemsDisplayed) {
            this.showMoreLinkElement = this.createShowMoreLink();
            this.itemsWrapper.appendChild(this.showMoreLinkElement);
            this.setShowMore(false);
        } else {
            this.items.forEach(item => show(item.element));
        }
    }

    private createShowMoreLink(): HTMLElement {
        const element = document.createElement('div');
        element.dataset.toggleState = 'collapsed';
        element.classList.add('show-more-link');
        element.innerHTML = this.showMoreText;
        element.addEventListener('click', () => this.toggleShowMore());
        return element;
    }

    private toggleShowMore(): void {
        this.setShowMore(!this.isShowMoreExpanded());
    }

    private isShowMoreExpanded(): boolean {
        return this.showMoreLinkElement?.dataset.toggleState === 'expanded';
    }

    private setShowMore(expanded: boolean): void {
        if (!this.showMoreLinkElement) return;

        if (expanded) {
            this.items.forEach(item => show(item.element));
            this.showMoreLinkElement.dataset.toggleState = 'expanded';
            this.showMoreLinkElement.classList.remove('show-more-link');
            this.showMoreLinkElement.classList.add('show-less-link');
            this.showMoreLinkElement.innerHTML = this.showLessText;
        } else {
            this.items.forEach((item, index) => {
                if (index >= this.defaultItemsDisplayed) {
                    hide(item.element);
                } else {
                    show(item.element);
                }
            });
            this.showMoreLinkElement.dataset.toggleState = 'collapsed';
            this.showMoreLinkElement.classList.remove('show-less-link');
            this.showMoreLinkElement.classList.add('show-more-link');
            this.showMoreLinkElement.innerHTML = this.showMoreText;
        }
    }
}
